/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, type Type, Component, Injector, Input, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import {
    type ActionAbstract, type Command, type HttpResponse,
    type PromptConfigFull,
    type Rest, DMValidators, FormControl, FormGroup, FormSlideWindowAbstract, SelectComponent, UNPLACEHOLDER,
} from '@dm/core';
import { FileSystemModeE } from 'app/common/constants';
import { SmartGuiService, TaskService, ToolService } from 'app/common/services';
import { type CIFSShare, type Dtree, type FileSystem, type HomedirRule, type Omtask } from 'app/common/types/mo';
import { type Observable, type Subscription, of } from 'rxjs';
import { finalize, flatMap } from 'rxjs/operators';

import { CreateFileSystemComponent } from '../../../../file-system/create-file-system/create-file-system.component';
import { FsSmartGuiInfoComponent } from '../../../../file-system/fs-smartgui-info/fs-smartgui-info.component';
import { type SmartguiCifsshareReceived } from '../../../share';
import { CifsShareService } from '../../cifs-share.service';
import { CifsShareStrService } from '../../cifs-share-str.service';
import { HomedirRuleCifsShareService } from '../homedir-rule-cifs-share.service';
import { DtreeService } from './../../../../dtree/dtree.service';

@Component({
    selector: 'app-common-add-homedir-rule-cifs-share',
    templateUrl: './add-homedir-rule-cifs-share.component.html',
    styleUrls: ['./add-homedir-rule-cifs-share.component.scss'],
})
export class AddHomedirRuleCifsShareComponent extends FormSlideWindowAbstract implements OnInit {
    i18n = this.str.i18n;
    @Input() vstoreId: string;
    @Input() vstoreName: string;
    /**
     * 前台表格传入true
     */
    @Input() isFrontTable = false;
    /**
     * 后台表格传入的当前创建的client所属的share
     */
    @Input() cifsShare: CIFSShare;
    // 是否支持智能GUI，默认支持
    @Input() supportSmartGui = true;

    @ViewChild('fsSelect') fsSelect: SelectComponent;

    UNPLACEHOLDER = UNPLACEHOLDER;

    closeArgs = {
        refresh: false,
        data: null,
    };

    /**
     * 侧划框忙等
     */
    pathLoading = false;

    vm: FormGroup;
    errorMessage: any;

    get vmValue(): HomedirRule {
        return this.vm ? this.vm.value : null;
    }

    fileSystemRest: Rest;
    dtreeRest: Rest;

    // 共享路径
    private _share_path = '';

    set share_path(value) {
        this._share_path = value;
        // 更新是否有\
        this.dtreePathHasBackslash = this.isDtreePathHasBackslash();
    }

    get share_path(): string {
        return this._share_path;
    }

    /**
     * dtree path 中是否含有反斜杠
     */
    dtreePathHasBackslash = false;

    // 是否显示dtree
    showDtree = true;

    selectedFilesystem: FileSystem;

    advanced = false;
    // 上一次选中的文件系统所属租户的vstoreId
    preFsVstoreId: string;
    // 创建文件系统按钮灰化标识
    createFsDisabled = false;

    // 从接口获取的NFS智能推荐数据
    cifsSmartguiData: SmartguiCifsshareReceived = {};

    createFsTaskId: string;
    createFs$: Subscription;

    constructor(
        injector: Injector,
        public str: CifsShareStrService,
        private CIFSService: CifsShareService,
        private service: HomedirRuleCifsShareService,
        private toolService: ToolService,
        private dtreeService: DtreeService,
        private smartguiService: SmartGuiService,
        private taskService: TaskService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        if (!this.checkDepPermission(['file_system_R'])) {
            this.close();
            return;
        }

        // 不支持审计日志文件系统和内部文件系统，过滤掉
        this.fileSystemRest = this.CIFSService.getBatchFSRest(this.vstoreId);
        const filters = [{ field: 'isAuditLogFs', matchMode: '::', value: 'false' }, {
            field: 'SUBTYPE', matchMode: '!!', value: FileSystemModeE.SMART_MOBILITY_INNER_FILE_SYSTEM,
        }];
        this.fileSystemRest.setFilters(filters);

        if (this.vstoreId) {
            this.preFsVstoreId = this.vstoreId;
        }
        super.ngOnInit();

        this.pageReady = true;
        this.windowVisible = true;

        // 如果不是从copyFrom和createas打开的则获取CIFS智能推荐数据
        if (this.supportSmartGui) {
        }
    }


    /**
     * 获取CIFS智能推荐数据
     */
    getCifsSmartguiData(): void {
        if (!this.smartguiService.switch) {
            return;
        }

        // 添加忙等效果
        this.showLoading();
        this.CIFSService.getSmartguiCIFS(this.vstoreId, { requestTimeoutTip: this.i18n.error.smartRecommendDataTimeoutMsg })
            .pipe(
                flatMap(({ data }) => {
                    this.cifsSmartguiData = {
                        ...this.cifsSmartguiData,
                        ...data,
                    };
                    this.vm.get('NAME').setValue(this.cifsSmartguiData.NAME[0]);
                    if (this.cifsSmartguiData.ENABLENOTIFY) {
                        this.vm.get('ENABLENOTIFY').setValue(this.cifsSmartguiData.ENABLENOTIFY === 'true');
                    }
                    if (this.cifsSmartguiData.ENABLECA) {
                        this.vm.get('ENABLECA').setValue(this.cifsSmartguiData.ENABLECA === 'true');
                    }
                    if (this.cifsSmartguiData.smb3encryptionenable) {
                        this.vm.get('smb3EncryptionEnable').setValue(this.cifsSmartguiData.smb3encryptionenable === 'true');
                    }
                    if (this.cifsSmartguiData.unencryptedaccess) {
                        this.vm.get('unencryptedAccess').setValue(this.cifsSmartguiData.unencryptedaccess === 'true');
                    }
                    if (this.cifsSmartguiData.ABEENABLE) {
                        this.vm.get('ABEENABLE').setValue(this.cifsSmartguiData.ABEENABLE === 'true');
                    }
                    if (this.cifsSmartguiData.ENABLESHOWSNAPSHOT) {
                        this.vm.get('ENABLESHOWSNAPSHOT').setValue(this.cifsSmartguiData.ENABLESHOWSNAPSHOT === 'true');
                    }
                    if (this.cifsSmartguiData.ENABLESHOWPREVIOUSVERSIONS) {
                        this.vm.get('ENABLESHOWPREVIOUSVERSIONS').setValue(this.cifsSmartguiData.ENABLESHOWPREVIOUSVERSIONS === 'true');
                    }
                    if (this.cifsSmartguiData && this.cifsSmartguiData.filesystem) {
                        const queryParams = {
                            name: this.cifsSmartguiData.filesystem,
                            vstoreId: this.vstoreId,
                        };
                        return this.CIFSService.getFSOnName(queryParams);
                    } else {
                        return of(null);
                    }
                }),
                finalize(() => this.hideLoading()),
            )
            .subscribe(({ data }) => {
                if (!data) {
                    return;
                }
                this.fsSelect.setFixedOption({
                    value: data.ID,
                    label: data.NAME,
                    data,
                });
            });
    }

    /**
     * 判断是否有\ \要转义才能判断
     */
    isDtreePathHasBackslash(): boolean {
        return this.share_path.indexOf('\\') !== -1;
    }

    initVm(): void {
        // 初始化默认数据
        const defaultShare: HomedirRule = {
            NAME: '',
            fsId: '',
            autoCreate: 'true',
            priority: 32,
        };

        this.vm = new FormGroup({
            NAME: new FormControl(defaultShare.NAME, {
                validators: [
                    Validators.required,
                    DMValidators.rangeByteLength(1, 255),
                    this.service.homedirMappingRuleValidator,
                ],
                updateOn: 'blur',
            }),
            fsId: new FormControl(defaultShare.fsId, [
                Validators.required,
            ]),
            dtree: new FormControl(null, [
            ]),
            priority: new FormControl(defaultShare.priority, [
            ]),
            autoCreate: new FormControl(defaultShare.autoCreate === 'true'),
        }, { updateOn: 'blur' });

        this.errorMessage = {
            NAME: {
                required: this.toolService.requiredErr(this.i18n.label.userName),
                rangeByteLength: this.toolService.rangeByteLengthErr(1, 255),
                homedirMappingRule: this.str.get(this.i18n.error.userName,
                    [this.i18n.label.userName]),
            },
            fsId: {
                required: this.toolService.requiredErr(this.i18n.label.fileSystem),
            },
            DESCRIPTION: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 255),
            },
        };
    }

    /**
     * 选择文件系统后，需要更新dtree和sharepath
     */
    onChooseFileSystem($event): void {
        if (!$event.value) {
            this.share_path = '';
            return;
        }
        this.share_path = `/${$event.option.data.NAME}/`;

        this.selectedFilesystem = $event.option.data;
        this.dtreeRest = this.CIFSService.getBatchDtreeRest(this.selectedFilesystem);
        // 清空dtree
        this.vm.get('dtree').setValue(null);

        const currentFsVstoreId = this.selectedFilesystem.vstoreId;

        this.preFsVstoreId = currentFsVstoreId;
    }

    /**
     * 选择dtree之后，需要更新sharepath
     */
    onChooseDtree($event): void {
        if (!$event.value) {
            this.share_path = `/${this.selectedFilesystem.NAME}/`;
            return;
        }
        // dtree的全路径是单个查的，这个时候要展示微观忙等
        this.pathLoading = true;
        this.CIFSService.getDtree($event.option.data.ID, this.selectedFilesystem.vstoreId).pipe(
            finalize(() => {
                this.pathLoading = false;
            }),
        ).subscribe(({ data }: { data: Dtree }) => {
            this.share_path = this.dtreeService.getDtreeFullPath(this.selectedFilesystem, data);
        });
    }

    /**
     * 创建文件系统
     */
    createFs(): void {
        this.createFsDisabled = true;

        let targetComponent: Type<ActionAbstract>;
        targetComponent = this.smartguiService.switch ? FsSmartGuiInfoComponent : CreateFileSystemComponent;
        this.doAction(targetComponent,
            {
                vstore: { label: this.vstoreName, value: this.vstoreId },
                onClose: ({ data }) => {
                    this.createFsDisabled = false;
                    if (!data) {
                        return;
                    }

                    if (data.NAME) {
                        this.addFsOptAndSelect(data);
                    } else if (data.taskId) {
                        this.initCreateFsTask(data);
                    }
                },
            },
        );
    }

    initCreateFsTask(data): void {
        this.createFsTaskId = data.taskId;
        if (this.createFs$) {
            this.createFs$.unsubscribe();
        }
        this.createFs$ = this.taskService.subscribe('fileSystem', (task, { id }: Omtask) => {
            if (id === this.createFsTaskId) {
                this.fsSelect.refresh();
            }
        });
    }

    /**
     * 文件系统下拉默认选中新建项
     * @param {*} data 后台返回的原始对象
     */
    addFsOptAndSelect(data: FileSystem): void {
        // 增加选项
        const newOption = {
            label: data.NAME,
            value: data.ID,
            data,
        };
        this.fsSelect.setFixedOption(newOption);
    }

    prompt(): PromptConfigFull[] {
        // 如果是前台表格，就不弹
        if (this.isFrontTable) {
            return [];
        }
        const prompts: PromptConfigFull[] = [
            {
                id: 'prompt-add-homedir-rule-cifs-share-setHomedirRule',
                opType: 'warning',
                title: this.i18n.title.addMappingRule,
                content: this.i18n.highRisk.setHomedirRule,
                width: 700,
            },
        ];

        return prompts;
    }

    collectionRuleParam(): HomedirRule {
        const vmValue = this.vmValue;
        return {
            NAME: vmValue.NAME,
            fsPath: this.share_path,
            priority: vmValue.priority,
            autoCreate: vmValue.autoCreate,
            vstoreId: this.vstoreId,
        };
    }

    buildCmds(): Command[] {
        const params = this.collectionRuleParam();
        // 如果是前台表格的话，不下发接口
        if (this.isFrontTable) {
            this.closeArgs.data = params;
            return [];
        } else {
            const cmds: Command[] = [this.buildHomedirRuleCmd(params)];
            return cmds;
        }
    }

    /**
     * 构建添加homedir 映射规则的rule
     */
    buildHomedirRuleCmd(param: HomedirRule): Command {
        return {
            name: this.i18n.rest.homedir.addRule,
            nameParams: [param.NAME, param.fsPath],
            action: (): Observable<HttpResponse> => {
                param.parentID = this.cifsShare.ID;
                return this.service.add(param);
            },
        };
    }

    /**
     * 复写ok函数，当pathloading的时候，不让点
     */
    onOk(): void {
        if (this.pathLoading) {
            return;
        }
        super.onOk();
    }
}
